import React, { useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import { PROVIDER_SECTION_BREADCRUMB } from './provider-breadcrumb'
import { IconLink } from '../../../../../shared/icon/icon'
import { useApiFormProviderConsent, useApiFormProviderGoogle, useApiListProviderConsentRequest } from '../../../../../api-new/state/auth'
import { useRoleSysProvider } from '../../../../../role/hook/role-sys'
import { useLoad } from '../../../../../shared/hooks/load'
import { useQueryParam } from '../../../../../shared/hooks/location'
import { WorkspaceSection } from '../../../../../shared/sso-workspace/workspace-shell'
import { ConnectProvider } from '../../../../../shared/sso-consent/connect-provider'
import { ConsentRequestList } from '../../../../../shared/sso-consent/consent-request-list'
import { OrgPicker } from '../../../../../shared/org-search/org-picker'
import { useOrganizationName } from '../../../../../shared/org-search/use-organization-names'

// Staff connect page (spec 2026-09-26): one flow instead of two bare forms.
// Choose Microsoft or Google; Microsoft records a consent request for the
// chosen organization (emailed to its IT administrator, or a link to copy)
// and the organization's requests are listed underneath. Google Workspace has
// no approval step: it creates the organization's connection, inactive until
// its domain is verified, and opens its workspace.
//
// ?organizationID=&tenant= prefill the form: the "Send a new request" link in
// an expired-request email, and the organization SSO tab's empty state, land
// here.
export const ProviderConnect = () => {
  const history = useHistory()

  const consent = useApiFormProviderConsent()
  const providerGoogle = useApiFormProviderGoogle()
  const roleSysProvider = useRoleSysProvider()

  const [organizationID, setOrganizationID] = useState(useQueryParam('organizationID') || '')
  const initialTenant = useQueryParam('tenant') || ''
  const [refresh, setRefresh] = useState(0)

  // The chosen organization's name (the picker searches content; never the
  // whole list).
  const organizationName = useOrganizationName(organizationID)

  const reload = () => setRefresh(n => n + 1)

  const handleRequest = ({ tenant, sendTo }) => consent.postArg('/entra/request', { organizationID: Number(organizationID), tenant, sendTo })
  const handleResend = async id => { const result = await consent.postArg(`/requests/${id}/resend`, {}); reload(); return result }
  const handleCancel = async id => { const result = await consent.postArg(`/requests/${id}/cancel`); reload(); return result }

  // Google Workspace for the chosen organization (audit B7): the domain is
  // claimed for it, the connection is owned by and linked to it, and it
  // stays inactive until the domain is verified or overridden and bound.
  // The workspace it lands on shows what is left to do.
  const handleGoogle = async ({ domain, name }) => {
    const connection = await providerGoogle.post({ organizationID: Number(organizationID), domain, providerName: name, displayName: name })
    history.push(`/admin/sso/connections/${connection.id}`)
  }

  const organizationPicker =
    <div className="sw-field mb-3">
      <label className="sw-label" htmlFor="sc-organization">Organization</label>
      <OrgPicker id="sc-organization" label="Organization" value={organizationID} disabled={!roleSysProvider.accessCreate}
        placeholder="Search for the organization the connection is for" onChange={setOrganizationID} />
    </div>

  return (
    <BreadcrumbView parent={PROVIDER_SECTION_BREADCRUMB} Icon={IconLink} text="connect">
      <div className="sw-workspace sw-pane">
        <header className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
          <div>
            <h1 className="sw-title">Connect single sign-on</h1>
            <div className="sw-sub">Connect an organization's Microsoft or Google accounts so its people sign in to EducationXR with them.</div>
          </div>
          <Link className="btn sw-btn" to="/admin/sso/consent-requests">All consent requests</Link>
        </header>

        <WorkspaceSection id="connect" title="Choose a provider">
          <ConnectProvider
            audience="staff"
            enabled={roleSysProvider.accessCreate}
            ready={!!organizationID}
            notReadyHint="Choose an organization first."
            initialTenant={initialTenant}
            lead={organizationPicker}
            onRequest={handleRequest}
            onRequested={reload}
            onResend={handleResend}
            onCancel={handleCancel}
            onGoogle={handleGoogle}
          />
        </WorkspaceSection>

        {organizationID &&
          <WorkspaceSection id="requests" title={organizationName ? `Consent requests for ${organizationName}` : 'Consent requests'}
            lede="Every approval request sent for this organization, newest first.">
            <OrganizationRequests key={`${organizationID}-${refresh}`} organizationID={organizationID} enabled={roleSysProvider.accessCreate} consent={consent} />
          </WorkspaceSection>}
      </div>
    </BreadcrumbView>
  )
}

// Keyed on the organization (and a refresh counter): useApiList freezes its
// query string when the hook is created, so a different organization is a
// fresh mount -- the same pattern as provider-domain-list.js's state filter.
const OrganizationRequests = ({ organizationID, enabled, consent }) => {
  const apiList = useApiListProviderConsentRequest(`?organizationID=${organizationID}`)

  useLoad(() => apiList.get(), [])

  return (
    <ConsentRequestList
      rows={apiList.items}
      loaded={apiList.loaded !== false}
      enabled={enabled}
      showOpenConnection
      emptyText="No requests yet. Send one above and it appears here with its status."
      onLink={id => consent.get(`requests/${id}/link`)}
      onResend={id => consent.postArg(`/requests/${id}/resend`, {})}
      onCancel={id => consent.postArg(`/requests/${id}/cancel`)}
      onChanged={() => apiList.get()}
    />
  )
}
